import React from 'react';
import { alpha, DialogProps, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import { useLiveTime } from 'src/hooks/useLiveTime';

const Root = styled('div')(({ theme }) => ({
    position: 'absolute',
    bottom: `calc(100% + ${theme.spacing(2)})`,
    left: 0,
    right: 0,
    display: 'flex',
    justifyContent: 'center',
    pointerEvents: 'none'
}));

const Pill = styled('div', {
    shouldForwardProp: prop => prop !== 'showBackground'
})<{ showBackground: boolean }>(({ theme, showBackground }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '120px',
    // Without the background there is nothing for the radius to round, and the text keeps whatever
    // colour the surrounding surface gives it.
    ...(showBackground && {
        padding: theme.spacing(0.5, 0),
        borderRadius: theme.shape.borderRadius,
        backgroundColor: alpha(theme.palette.common.black, 0.9),
        color: theme.palette.common.white
    })
}));

interface LiveClockPillProps {
    /** The dark pill reads over a page; turn it off when the clock sits on a surface of its own. */
    showBackground?: boolean;
}

export const LiveClockPill: React.FC<LiveClockPillProps> = ({ showBackground = true }) => {
    const now = useLiveTime();
    const timeFormat = React.useMemo(() => new Intl.DateTimeFormat(undefined, { timeStyle: 'medium' }), []);
    const dateFormat = React.useMemo(() => new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }), []);
    return (
        <Pill showBackground={showBackground}>
            <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {dateFormat.format(now)}
            </Typography>
            <Typography variant="h5" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {timeFormat.format(now)}
            </Typography>
        </Pill>
    );
};

export const dialogLiveClockSlotProps: DialogProps['slotProps'] = {
    paper: { sx: { overflow: 'visible' } }
};

/**
 * A live clock that sits just above the dialog it is rendered in
 * The date and time are formatted for the device's own locale, so they follow the reader's
 * conventions — including whether the time is shown on a 12 or 24 hour clock.
 */
export const DialogLiveClock: React.FC = () => (
    <Root aria-hidden>
        <LiveClockPill />
    </Root>
);
